/**
 * components/riskTerminal/controls.jsx
 *
 * The Risk Terminal's action controls (upstox-037), built from the design's
 * own pieces (handover/design/extracted/): the regroup picker (dashed panel,
 * target buttons, "+ NEW STRUCTURE", CANCEL, the note under it), the rename
 * input, the grey note box, and the order-entry form's pick buttons. Each
 * action returns the server's answer; a refusal is shown here, in place.
 *
 * `initial` props seed a control's inner state — used only by the dev
 * harness to photograph a state (upstox-037 screenshots).
 */

import React, { useEffect, useState } from 'react';
import { C, FONT } from './theme';

export const pickerStyle = { margin: '4px 14px 8px 40px', padding: '10px 12px', background: '#fff', border: '1px dashed ' + C.line3 };
export const pickerHeadStyle = { fontSize: '10px', color: C.ink4, letterSpacing: '0.08em', marginBottom: '8px' };
export const targetStyle = { height: '28px', padding: '0 11px', background: '#fff', color: C.ink, border: '1px solid ' + C.line4, fontFamily: FONT, fontSize: '10px', letterSpacing: '0.04em', cursor: 'pointer' };
export const darkBtnStyle = { height: '28px', padding: '0 11px', background: C.ink, color: '#fff', border: '1px solid ' + C.ink, fontFamily: FONT, fontSize: '10px', letterSpacing: '0.06em', cursor: 'pointer', fontWeight: 600 };
export const cancelStyle = { height: '28px', padding: '0 10px', background: 'transparent', color: C.ink3, border: '1px solid ' + C.line3, fontFamily: FONT, fontSize: '10px', letterSpacing: '0.06em', cursor: 'pointer' };
export const nameInputStyle = { height: '28px', width: '260px', background: '#fff', border: '1px solid ' + C.ink, color: C.ink, fontFamily: FONT, fontSize: '15px', fontWeight: 500, padding: '0 8px', outline: 'none' };
export const noteBoxStyle = { margin: '6px 14px 8px 14px', padding: '9px 12px', border: '1px solid ' + C.line, background: C.paper2, fontSize: '11px', color: C.ink2, lineHeight: 1.5 };

const busyLook = b => (b ? { opacity: 0.5, cursor: 'wait' } : {});

/** The server's refusal, where the action was taken. */
export function Refusal({ text, style }) {
  if (!text) return null;
  return (
    <div role="alert" style={{ fontSize: '11px', color: C.ink, background: C.redTint, border: '1px solid ' + C.red, padding: '7px 10px', lineHeight: 1.45, marginTop: '8px', ...style }}>
      <span style={{ fontSize: '9px', letterSpacing: '0.1em', color: C.red, fontWeight: 700, marginRight: '8px' }}>REFUSED</span>{text}
    </div>
  );
}

/** A note after a successful action (the design's grey note box). */
/**
 * A success note. upstox-053: with `fade`, it goes by itself after
 * NOTE_FADE_MS — no OK needed; hovering keeps it open (the timer restarts on
 * leaving). Refusals are a separate component and stay until closed.
 */
export const NOTE_FADE_MS = 5000;
export function Note({ text, onClose, style, fade, hold }) {
  const [hover, setHover] = useState(false);
  useEffect(() => {
    if (!text || !fade || hover || hold || !onClose) return undefined;
    const t = setTimeout(onClose, NOTE_FADE_MS);
    return () => clearTimeout(t);
  }, [text, fade, hover, hold]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!text) return null;
  return (
    <div data-note style={{ ...noteBoxStyle, display: 'flex', gap: '10px', alignItems: 'flex-start', ...style }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div style={{ flex: 1 }}>{text}</div>
      {onClose && !fade && <button type="button" onClick={onClose} style={{ ...cancelStyle, height: '20px', fontSize: '9px' }}>OK</button>}
    </div>
  );
}

/**
 * The design's regroup picker. options: [{key, label, run: () => Promise<result>}];
 * newLabel + create(name) → Promise<result> for "+ NEW …" (a name is typed first).
 */
export function Picker({ head, options, newLabel, create, onCancel, onDone, note, amber, initial, style }) {
  const [naming, setNaming] = useState(initial?.naming || false);
  const [name, setName] = useState(initial?.name || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(initial?.error || null);
  const go = async fn => {
    if (busy) return;
    setBusy(true); setErr(null);
    const res = await fn();
    setBusy(false);
    if (res && res.ok) onDone && onDone(res); else setErr(res ? res.message : 'Refused.');
  };
  return (
    <div style={{ ...pickerStyle, ...style }} onClick={e => e.stopPropagation()}>
      <div style={pickerHeadStyle}>{head}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
        {options.map(o => (
          <button key={o.key} type="button" disabled={busy} style={{ ...targetStyle, ...busyLook(busy) }} onClick={() => go(o.run)}>{o.label}</button>
        ))}
        {create && !naming && <button type="button" disabled={busy} style={{ ...darkBtnStyle, ...busyLook(busy) }} onClick={() => setNaming(true)}>{newLabel}</button>}
        {create && naming && (
          <>
            <input autoFocus value={name} placeholder="NAME" onChange={e => setName(e.target.value)} style={{ ...nameInputStyle, width: '220px', height: '28px', fontSize: '13px' }}
              onKeyDown={e => { if (e.key === 'Enter' && name.trim()) go(() => create(name.trim())); if (e.key === 'Escape') setNaming(false); }} />
            <button type="button" disabled={busy || !name.trim()} style={{ ...darkBtnStyle, ...busyLook(busy), ...(name.trim() ? {} : { opacity: 0.45, cursor: 'not-allowed' }) }} onClick={() => go(() => create(name.trim()))}>CREATE AND MOVE</button>
          </>
        )}
        <button type="button" style={cancelStyle} onClick={onCancel}>CANCEL</button>
      </div>
      {note && <div style={{ fontSize: '11px', color: amber ? C.amber : C.ink3, fontWeight: amber ? 500 : 400, marginTop: '8px', lineHeight: 1.5 }}>{note}</div>}
      <Refusal text={err} />
    </div>
  );
}

/** Close refused while legs are open: the server's list, then an explicit second step. */
export function ClosePanel({ name, message, legs, symbolOf, forceClose, onCancel, onDone, initialError }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(initialError || null);
  const go = async () => {
    setBusy(true); setErr(null);
    const res = await forceClose();
    setBusy(false);
    if (res.ok) onDone(res); else setErr(res.message);
  };
  return (
    <div style={pickerStyle} onClick={e => e.stopPropagation()}>
      <div style={pickerHeadStyle}>{'CLOSE ' + name + ' — REFUSED WHILE LEGS ARE OPEN'}</div>
      <div style={{ fontSize: '11px', color: C.ink2, lineHeight: 1.5, marginBottom: '8px' }}>{message}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: C.line2, border: '1px solid ' + C.line2, marginBottom: '10px' }}>
        {(legs || []).map(l => (
          <div key={l.id} style={{ display: 'flex', padding: '5px 10px', background: '#fff', fontSize: '12px', color: C.ink }}>{symbolOf(l.instrument_key)}</div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" disabled={busy} style={{ ...darkBtnStyle, background: C.red, borderColor: C.red, ...busyLook(busy) }} onClick={go}>CLOSE ANYWAY — LEGS RETURN TO UNCLASSIFIED</button>
        <button type="button" style={cancelStyle} onClick={onCancel}>CANCEL</button>
      </div>
      <Refusal text={err} />
    </div>
  );
}

const INTENTS = ['BUY', 'SELL', 'HEDGE', 'ADJUST', 'EXIT'];

/** The five intents, BUY/SELL only where they match the order's side. */
export function intentsFor(side) {
  const s = String(side || '').trim().toUpperCase();
  return INTENTS.filter(i => (i === 'BUY' ? s === 'BUY' : i === 'SELL' ? s === 'SELL' : true));
}

/**
 * An order with no declared intent: TAG INTENT → pick → "Intent is permanent"
 * confirmation → declare. `open`/`onOpen`/`onClose` let the parent keep one open at a time.
 */
export function IntentControl({ order, open, onOpen, onClose, declare, onDone, initial, slot }) {
  const [chosen, setChosen] = useState(initial?.chosen || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(initial?.error || null);
  const pickStyle = { height: '26px', padding: '0 10px', background: '#fff', color: C.ink3, border: '1px solid ' + C.line3, fontFamily: FONT, fontSize: '10px', letterSpacing: '0.06em', cursor: 'pointer' };
  // upstox-069: the open-order row puts a small "TAG" at the right of line 1 (slot 'button') and the
  // choice under the row (slot 'panel'); with no slot the control is whole, as before
  if (slot === 'button') {
    return <button type="button" data-tag-btn onClick={open ? onClose : onOpen} style={{ ...pickStyle, height: '20px', padding: '0 7px', fontSize: '9px', color: C.amber, borderColor: C.amberLine, background: 'transparent', fontWeight: 600 }} data-tip="Declare this order's intent — once, permanently">TAG</button>;
  }
  if (slot === 'panel' && !open) return null;
  if (!open) {
    return <button type="button" onClick={onOpen} style={{ ...pickStyle, height: '22px', fontSize: '9px', color: C.amber, borderColor: C.amberLine, fontWeight: 600 }} data-tip="Declare this order's intent — once, permanently">TAG INTENT</button>;
  }
  const go = async () => {
    setBusy(true); setErr(null);
    const res = await declare(order.upstox_order_id, chosen);
    setBusy(false);
    if (res.ok) { onDone && onDone(res); onClose(); } else setErr(res.message);
  };
  return (
    <div style={{ marginTop: '6px', padding: '8px 10px', border: '1px dashed ' + C.line3, background: '#fff' }} onClick={e => e.stopPropagation()}>
      {!chosen ? (
        <>
          <div style={{ fontSize: '10px', color: C.ink4, letterSpacing: '0.1em', fontWeight: 500, marginBottom: '6px' }}>INTENT · {String(order.side || '').toUpperCase()} ORDER</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {intentsFor(order.side).map(i => <button key={i} type="button" style={pickStyle} onClick={() => setChosen(i)}>{i}</button>)}
            <button type="button" style={{ ...cancelStyle, height: '26px' }} onClick={onClose}>CANCEL</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize: '11px', letterSpacing: '0.1em', color: C.amber, fontWeight: 700 }}>INTENT IS PERMANENT</div>
          <div style={{ fontSize: '12px', color: C.ink2, lineHeight: 1.45, margin: '4px 0 8px 0' }}>Tag this {String(order.side || '').toUpperCase()} order as <b>{chosen}</b>? A declared intent can never be changed.</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button type="button" disabled={busy} style={{ ...darkBtnStyle, ...busyLook(busy) }} onClick={go}>DECLARE {chosen}</button>
            <button type="button" style={cancelStyle} onClick={() => { setChosen(null); setErr(null); }}>BACK</button>
          </div>
        </>
      )}
      <Refusal text={err} />
    </div>
  );
}
